<script setup lang="ts">
import { STATE_CONTENT_ADDRESS_SYSTEM } from '@/constants/statesOfAddressSystem'

import { useAddressesStore } from '@/store/addresses/addresses'
import {
  useAddressSystemMapStore,
  useAddressSystemStateStore,
  useAddressSystemStore,
} from '@/store/addressSystem'
import { useBasketStore } from '@/store/basket/basket'
import { useBasketPaymentMethodsStore } from '@/store/basket/basket-payment-methods'
import { useBasketPointStore } from '@/store/basket/basket-point'

import {
  DeliveryMethods,
  SetDeliveryMethodPayload,
} from '@/api/modules/basket/types'
import type { DialogContentState } from '@/types/address-system'
import { BasketPoint } from '@/types/api/api-v3/basket-points'

import AddressSystemActions from '@/components/AddressSystem/AddressSystemActions.vue'
import AddressSystemHeadline from '@/components/AddressSystem/AddressSystemHeadline.vue'
import AddressSystemSearch from '@/components/AddressSystem/search/AddressSystemSearch.vue'
import AddressSystemUser from '@/components/AddressSystem/user/AddressSystemUser.vue'
import BasketAddressesTabs from '@/components/Basket/basket-addresses/BasketAddressesTabs.vue'
import BasketAddressesWindows from '@/components/Basket/basket-addresses/BasketAddressesWindows.vue'

type Props = {
  stateDialogContent: DialogContentState | undefined
  deliveryBasket: SetDeliveryMethodPayload
}

const props = defineProps<Props>()

const emit = defineEmits(['onChangeBasketAddress'])

const basketStore = useBasketStore()
const addressSystemStore = useAddressSystemStore()
const pointStore = useBasketPointStore()
const addressesStore = useAddressesStore()
const basketPaymentMethodsStore = useBasketPaymentMethodsStore()
const { changeDialogContent } = useAddressSystemStateStore()
const { pickupPoints, hallPoints } = storeToRefs(useAddressSystemMapStore())

const { isMobile } = useResponsive()
const { $metrics } = useNuxtApp()

const courierPoints = computed(() => addressesStore.addresses.data)

const isStateDialogNew = computed(
  () => props.stateDialogContent === STATE_CONTENT_ADDRESS_SYSTEM.NEW,
)

const countPoints = computed(() => {
  // Чтобы все точки на карте отображались и в списке
  if (isStateDialogNew.value) return pointsList.value.length

  return pointStore.data.length
})

const currentPickupPoints = computed(() =>
  isStateDialogNew.value ? pickupPoints.value : pointStore.pickupPoints,
)
const currentHallPoints = computed(() =>
  isStateDialogNew.value ? hallPoints.value : pointStore.hallPoints,
)

// TODO [basket-refactoring]
// архитектурная проблема, что "самовывоз" и "в ресторане" - это одна модель и список, а "Доставка" - другая/другой
// Поэтому приходится везде писать проверки на тип DeliveryMethod
const allPoints = computed(() => {
  return {
    [DeliveryMethods.COURIER]: courierPoints.value,
    [DeliveryMethods.PICKUP]: currentPickupPoints.value,
    [DeliveryMethods.HALL]: currentHallPoints.value,
  }
})

// Список точек для выбора нового адреса
const pointsList = computed(() => {
  // При "Доставка" отдаем пустой массив
  if (props.deliveryBasket.delivery_method === DeliveryMethods.COURIER) {
    return []
  }

  // Выбранный адрес показываем всегда сверху
  return allPoints.value[props.deliveryBasket.delivery_method].sort((point) =>
    point.id === props.deliveryBasket.point_id ? -1 : 0,
  )
})

watch(
  () => pointsList.value,
  (newList) => {
    // Если в списке есть выбранный адрес - ничего не делаем
    const isSelectedAddressExist =
      props.deliveryBasket.point_id &&
      newList.some((point) => point.id === props.deliveryBasket.point_id)

    if (isSelectedAddressExist) return

    const payload = props.deliveryBasket

    delete payload.point_id

    // Сбрасываем выбранный адрес, если его нет в списке
    addressSystemStore.changeDeliveryBasket(payload)
  },
)

/* Tabs */
const isShowPickupTab = computed(() =>
  Boolean(currentPickupPoints.value.length),
)
const isShowHallTab = computed(() => Boolean(currentHallPoints.value.length))

const componentClass = computed(() => {
  const BASE_CLASS = 'address-system-delivery'
  return [BASE_CLASS, `${BASE_CLASS}--${props.stateDialogContent}`]
})

/* Windows */
const selectedAddress = computed(() => {
  const selectedAddressId = props.deliveryBasket.point_id || null

  // Ищем адрес целиком
  return allPoints.value[props.deliveryBasket.delivery_method].find(
    ({ id }) => id === selectedAddressId,
  )
})

const pushMetrics = (deliveryMethod: DeliveryMethods) => {
  const targetEvent =
    deliveryMethod === DeliveryMethods.HALL
      ? 'delivery_type_in_rest'
      : 'delivery_type_pickup'

  $metrics?.eventSendGoal({ ym: targetEvent })
}

const deliveryMethodChanged = () => {
  // Устанавливаем первый из списка оплат
  if (basketPaymentMethodsStore.data.length)
    basketStore.setSelectedPaymentMethod(basketPaymentMethodsStore.data[0])
}

// Обновляем выбранный способ при смене способа доставки
const setDeliveryMethod = (newDeliveryMethod: DeliveryMethods) => {
  addressSystemStore.changeDeliveryBasket({
    delivery_method: newDeliveryMethod,
  })

  // Фиксируем только способ доставки
  setSelectedAddress({ delivery_method: newDeliveryMethod })

  addressSystemStore.resetDeliveryAddress()

  if (newDeliveryMethod === DeliveryMethods.COURIER) return

  pushMetrics(newDeliveryMethod)
}

// Обновляем локально выбранный способ и id адреса при клике на адрес
const setSelectedAddress = (value: SetDeliveryMethodPayload) => {
  addressSystemStore.changeDeliveryBasket(value)
}

// Метод смены способа доставки и адреса в корзине
const onChangeBasketAddress = () => {
  emit('onChangeBasketAddress', onChangeBasketAddress)
}

// Если ресторан другого города - показываем модалку с редиректом на другой город
const onOpenDialogRedirectCity = (basketPoint: BasketPoint) => {
  const payload: SetDeliveryMethodPayload = {
    delivery_method: props.deliveryBasket.delivery_method,
    point_id: basketPoint.id,
    city_domain_prefix: basketPoint?.city_domain_prefix,
    order_address: basketPoint?.address,
  }

  changeDialogContent(STATE_CONTENT_ADDRESS_SYSTEM.REDIRECT)
  addressSystemStore.changeDeliveryBasket(payload)
}
</script>

<template>
  <div :class="componentClass">
    <address-system-headline
      v-if="!isMobile"
      :is-check-pickup="isShowPickupTab"
      :is-check-hall="isShowHallTab"
    />

    <basket-addresses-tabs
      :key="stateDialogContent"
      class="address-system-delivery__tabs"
      is-show-hall-tab
      is-show-pickup-tab
      :model-value="deliveryBasket.delivery_method"
      @update:model-value="setDeliveryMethod"
    />

    <basket-addresses-windows
      effect-shadow
      class="address-system-delivery__windows"
      :delivery-method="deliveryBasket.delivery_method"
      :max-count-of-visible-addresses="countPoints"
      :selected-address="selectedAddress"
      :points-list="pointsList"
      list-with-animation
      without-validate
      @on-set-selected-address="setSelectedAddress"
      @on-select-delivery-courier="setDeliveryMethod"
      @on-change-delivery-method="deliveryMethodChanged"
      @open-dialog-redirect-city="onOpenDialogRedirectCity"
    >
      <address-system-user
        v-if="stateDialogContent === STATE_CONTENT_ADDRESS_SYSTEM.SELECT"
        :delivery-basket="deliveryBasket"
        @on-change-selected-address="setSelectedAddress"
      />

      <address-system-search v-if="isStateDialogNew" />
    </basket-addresses-windows>

    <address-system-actions
      class="address-system-delivery__actions"
      :delivery-basket="deliveryBasket"
      @on-change-basket-address="onChangeBasketAddress"
    />
  </div>
</template>

<style scoped lang="scss">
.address-system-delivery {
  display: flex;
  flex-direction: column;

  @include media('>tablet') {
    padding: 50px 30px 30px;
  }

  &__tabs {
    min-height: 40px;
    margin-bottom: 24px;
  }

  &__windows {
    position: relative;
    display: flex;
    flex-direction: column;
    width: initial;
    overflow: hidden;

    :deep(.basket-pickup) {
      overflow-y: hidden;
    }

    :deep(.basket-hall) {
      overflow-y: hidden;
    }
  }
}

.address-system-delivery--new {
  width: 100%;

  :deep(.container-shadow--active) {
    height: 210px;
  }

  @include media('>tablet') {
    width: 410px !important;
  }

  @include media('>=portrait-phone') {
    width: 100%;

    :deep(.container-shadow--active) {
      height: 290px;
    }
  }

  .address-system-delivery__actions {
    margin-top: 20px;

    @include media('>tablet') {
      margin: auto 0 0 0;
    }
  }
}

.address-system-delivery--select {
  width: 100%;

  @include media('>tablet') {
    width: 510px !important;
  }

  :deep(.container-shadow--active) {
    height: 317px;
  }

  .address-system-delivery__actions {
    margin-top: 20px;

    @include media('>tablet') {
      margin: auto auto 0 auto;
    }
  }
}
</style>
